/**
 * Geraete-Datenbank fuer die Pixeldichte-Erkennung.
 *
 * Warum ueberhaupt eine Datenbank: Ein Browser verraet nicht, wie gross sein
 * Display physikalisch ist. Er kennt nur Aufloesung und devicePixelRatio. Um von
 * dort zu echten Zentimetern zu kommen, braucht es die Pixeldichte (ppi) - und
 * die muss man nachschlagen oder schaetzen.
 *
 * Zwei Schluessel, absteigend nach Verlaesslichkeit:
 *  1. Das Geraetemodell aus den User-Agent Client Hints. Nur Android/Chromium
 *     liefert das, dafuer eindeutig.
 *  2. Die Hardware-Aufloesung im Hochformat. Bei iPhones praktisch eindeutig,
 *     bei Android mehrdeutig - 1080x1920 kann ein 4,7"- oder ein 6"-Geraet sein.
 *
 * Die Liste deckt die verbreitetsten Geraete ab, nicht alle. Was fehlt, faellt
 * auf die Heuristik in dpi.ts zurueck. Erweitern ist billig: eine Zeile.
 */

/** Aufloesung im Hochformat, als "breite x hoehe" in Hardware-Pixeln. */
export const PPI_BY_RESOLUTION: Record<string, number> = {
  // --- Apple iPhone -------------------------------------------------------
  '640x1136': 326, // SE (1. Gen), 5s
  '750x1334': 326, // 6, 6s, 7, 8, SE (2./3. Gen)
  '828x1792': 326, // XR, 11
  '1080x1920': 401, // 6/7/8 Plus (rendert 1242x2208, skaliert auf 1080x1920)
  '1125x2436': 458, // X, XS, 11 Pro
  '1242x2688': 458, // XS Max, 11 Pro Max
  '1080x2340': 476, // 12 mini, 13 mini
  '1170x2532': 460, // 12, 12 Pro, 13, 13 Pro, 14
  '1284x2778': 458, // 12 Pro Max, 13 Pro Max, 14 Plus
  '1179x2556': 460, // 14 Pro, 15, 15 Pro, 16
  '1290x2796': 460, // 14 Pro Max, 15 Plus, 15 Pro Max, 16 Plus
  '1206x2622': 460, // 16 Pro
  '1320x2868': 460, // 16 Pro Max
  '1206x2662': 460, // 17 Pro (Naeherung, gleiche Dichteklasse)

  // --- Apple iPad ---------------------------------------------------------
  '1536x2048': 264, // iPad 9,7"/10,2" Retina, iPad Pro 9,7"
  '1620x2160': 264, // iPad 10,2" (9. Gen)
  '1640x2360': 264, // iPad Air 10,9", iPad 10,9"
  '1668x2388': 264, // iPad Pro 11"
  '2048x2732': 264, // iPad Pro 12,9"/13"

  // --- Android, eindeutige Aufloesungen -----------------------------------
  '1440x2960': 522, // Galaxy S8+/S9+, Note 8/9
  '1440x3040': 498, // Galaxy S10+, Note 10+
  '1440x3088': 522, // Galaxy S20+/S21 Ultra
  '1440x3200': 515, // Galaxy S20 Ultra, S22 Ultra
  '1440x3120': 512, // Pixel 7 Pro, Pixel 6 Pro
  '1344x2992': 489, // Pixel 8 Pro
  '1280x2856': 495, // Pixel 9 Pro XL
  '1008x2244': 429, // Pixel 9 Pro (klein)
  '1220x2712': 428, // Pixel 8 Pro (alternative Meldung)
  '1080x2412': 422, // Pixel 8a, Pixel 7a
  '1080x2280': 432, // Pixel 4, diverse Mittelklasse
  '1080x2400': 411, // sehr verbreitet: S21, A5x, Xiaomi-Mittelklasse
  '1080x2246': 409, // Pixel 3 XL
  '720x1600': 270, // Einsteiger-Android 6,5"
  '720x1520': 269, // Einsteiger-Android 6,1"
}

/**
 * Nachschlag ueber das Geraetemodell aus den Client Hints.
 * Schluessel sind kleingeschrieben und ohne Leerzeichen normalisiert.
 */
export const PPI_BY_MODEL: Record<string, number> = {
  // Google Pixel
  pixel6: 411,
  'pixel6a': 429,
  'pixel6pro': 512,
  pixel7: 416,
  'pixel7a': 429,
  'pixel7pro': 512,
  pixel8: 428,
  'pixel8a': 430,
  'pixel8pro': 489,
  pixel9: 422,
  'pixel9pro': 495,
  'pixel9proxl': 486,

  // Samsung Galaxy S
  'sm-g991b': 421, // S21
  'sm-g996b': 394, // S21+
  'sm-g998b': 515, // S21 Ultra
  'sm-s901b': 425, // S22
  'sm-s906b': 393, // S22+
  'sm-s908b': 500, // S22 Ultra
  'sm-s911b': 425, // S23
  'sm-s916b': 393, // S23+
  'sm-s918b': 500, // S23 Ultra
  'sm-s921b': 416, // S24
  'sm-s926b': 393, // S24+
  'sm-s928b': 505, // S24 Ultra
  'sm-s931b': 416, // S25
  'sm-s938b': 498, // S25 Ultra
}

/** Typische physikalische Displaybreite im Hochformat, in Zoll, je Geraeteklasse. */
export const FALLBACK_SCREEN_WIDTH_INCHES = {
  /**
   * Handys liegen erstaunlich eng beieinander: iPhone 14 misst 2,54",
   * Galaxy S23 2,54", Pixel 7 2,60", iPhone 15 Pro Max 2,80". Der Mittelwert
   * liegt bei rund 2,6" und trifft die meisten Geraete auf unter 8 % genau.
   */
  phone: 2.6,
  /** Tablets streuen staerker; 6,2" entspricht einem iPad Air im Hochformat. */
  tablet: 6.2,
} as const
